import { action } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { Types } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import { ConfigEditorViewModelBase } from './ConfigEditorViewModelBase'
import type { PendingConfig } from './legend-options'

/**
 * Whether requests start pending, and when they may advance.
 */
export class PendingConfigEditorViewModel extends ConfigEditorViewModelBase<PendingConfig> {
  private readonly _schema: IValidationSchema<PendingConfigEditorViewModel> = {
    enabledExpr: [
      func(() => this.enabledExpr.analyze(), 'There are formula errors.'),
    ],
    advanceDateExpr: [
      func(() => this.advanceDateExpr.analyze(), 'There are formula errors.'),
    ],
  }

  /**
   * Whether a request starts pending.
   */
  readonly enabledExpr: RynoEditorViewModel

  /**
   * When a pending request may advance.
   */
  readonly advanceDateExpr: RynoEditorViewModel

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies)
    this.enabledExpr = new RynoEditorViewModel({
      getSymbolTable: () => this._symbolTable,
      getNeededType: () => Types.boolean,
    })
    this.advanceDateExpr = new RynoEditorViewModel({
      getSymbolTable: () => this._symbolTable,
      getNeededType: () => Types.date,
    })
    this.lifecycle.manage(this.enabledExpr)
    this.lifecycle.manage(this.advanceDateExpr)
  }

  /**
   * Replaces the advance date with an example: January 12th, of this year if
   * it's still ahead, otherwise of next year.
   */
  @action.bound
  applyFixedDateTemplate() {
    const month = 1
    const day = 12
    const now = new Date()
    const thisYear = now.getFullYear()
    const year =
      now < new Date(thisYear, month - 1, day) ? thisYear : thisYear + 1
    this.advanceDateExpr.setValue(
      `
# Using the Time module, build an exact date from year, month and day.
Time.Of(
  # The year.
  ${year}
  # The month, where 1 is January.
  ${month}
  # The day of the month.
  ${day}
)
      `.trim(),
    )
  }

  /**
   * Replaces the advance date with an example: 3 months from submission.
   */
  @action.bound
  applyRelativeDateTemplate() {
    this.advanceDateExpr.setValue(
      `
# Using the Time module, add 3 months to the request's submission date.
Time.AddMonths(
  # Refers to the request's submission date.
  Request.DateSubmitted
  # The amount of months to add to the aforementioned date.
  3
)
      `.trim(),
    )
  }

  /** @inheritDoc */
  @action
  hydrate(config: PendingConfig | undefined) {
    this.enabledExpr.setValue(config?.enabledExpr)
    this.advanceDateExpr.setValue(config?.advanceDateExpr)
  }

  /** @inheritDoc */
  toJSON(): PendingConfig {
    return {
      enabledExpr: this.enabledExpr.toJSON(),
      advanceDateExpr: this.advanceDateExpr.toJSON(),
    }
  }
}
